import { useMemo } from 'react';

interface Props {
  values: number[];
  color: 'success' | 'danger' | 'accent' | 'neutral';
  height?: number;
  /** Whether to draw a baseline at 0 (для signed series). */
  showBaseline?: boolean;
}

const COLOR_STROKE: Record<Props['color'], string> = {
  success: 'var(--green)',
  danger: 'var(--red)',
  accent: 'var(--accent)',
  neutral: 'var(--text2)',
};

const COLOR_FILL_OPACITY = 0.18;

export function Sparkline({ values, color, height = 36, showBaseline = false }: Props) {
  const path = useMemo(() => buildPath(values, height), [values, height]);

  if (values.length === 0) return <div style={{ height }} />;

  const stroke = COLOR_STROKE[color];

  return (
    <svg
      viewBox={`0 0 ${path.width} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      className="block overflow-visible"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`sl-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={COLOR_FILL_OPACITY} />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>

      {showBaseline && path.zeroY !== null && (
        <line
          x1={0}
          x2={path.width}
          y1={path.zeroY}
          y2={path.zeroY}
          stroke="var(--border)"
          strokeWidth={1}
          strokeDasharray="2 3"
        />
      )}

      <path d={path.area} fill={`url(#sl-${color})`} />
      <path
        d={path.line}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Точка на последнем значении. НЕ <circle>: при preserveAspectRatio="none"
          геометрия (r, fill) растягивается по X вместе с viewBox — при малом числе
          точек кружок превращался в широкую «палку». Нулевой штрих со скруглённым
          концом + non-scaling-stroke рисуется в ЭКРАННЫХ пикселях и не искажается. */}
      {path.lastPoint && (
        <>
          <path
            d={`M ${path.lastPoint.x} ${path.lastPoint.y} l 0.0001 0`}
            fill="none"
            stroke="var(--bg2)"
            strokeWidth={8}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={`M ${path.lastPoint.x} ${path.lastPoint.y} l 0.0001 0`}
            fill="none"
            stroke={stroke}
            strokeWidth={5}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
    </svg>
  );
}

interface PathResult {
  width: number;
  line: string;
  area: string;
  lastPoint: { x: number; y: number } | null;
  zeroY: number | null;
}

function buildPath(values: number[], height: number): PathResult {
  const n = values.length;
  if (n === 0) return { width: 0, line: '', area: '', lastPoint: null, zeroY: null };

  // Определяем мин/макс с учётом «впихнуть нулевую линию», если знак меняется или включает 0.
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    min = min - 1;
    max = max + 1;
  }
  // Padding 8% сверху/снизу
  const pad = (max - min) * 0.08;
  min -= pad;
  max += pad;

  const width = Math.max(n - 1, 1) * 4; // 4px между точками — условный, viewBox растягивается
  const yScale = (v: number) => height - ((v - min) / (max - min)) * height;
  const xScale = (i: number) => (n > 1 ? (i / (n - 1)) * width : width / 2);

  const points = values.map((v, i) => ({ x: xScale(i), y: yScale(v) }));

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');

  // Area = line + закрытие к низу
  const area =
    `${line} L ${points[points.length - 1]!.x.toFixed(2)} ${height} L ${points[0]!.x.toFixed(2)} ${height} Z`;

  const lastPoint = points[points.length - 1] ?? null;

  // Y-координата нулевой линии (если 0 в диапазоне)
  const zeroY = min <= 0 && max >= 0 ? yScale(0) : null;

  return { width, line, area, lastPoint, zeroY };
}
